import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonImg,
    IonInput,
    IonItem,
    IonList,
    IonMenuButton,
    IonPage,
    IonText,
    IonTitle,
    IonToolbar,
    useIonToast,
} from '@ionic/react';
import { useAtom, useSetAtom } from 'jotai';
import { useRef, useState } from 'react';
import madper from '../assets/images/madper.png';
import tetra from '../assets/images/tetra.png';
import { defaultToastOptions, useResetAlert, loadingAtom, usersAtom } from '../utils/Global';
import odoo from '../utils/odoo';
import { toString } from 'lodash/fp';
import { BottomSlotWarning } from '../App';
import { captureMessage } from '@sentry/capacitor';
import { getMetaData } from '../utils/odooUtils';

const Login: React.FC = () => {
    const [user, setUser] = useAtom(usersAtom);
    const [presentToast] = useIonToast();
    const setLoading = useSetAtom(loadingAtom);
    const handleResetAlert = useResetAlert();

    const [usernameError, setUsernameError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [specialMode, setSpecialMode] = useState(false);

    const refClickNumber = useRef(0);
    const refUsername = useRef<HTMLIonInputElement>(null);
    const refPassword = useRef<HTMLIonInputElement>(null);
    const refUrl = useRef<HTMLIonInputElement>(null);
    const refDb = useRef<HTMLIonInputElement>(null);

    const login = async (e: React.FormEvent) => {
        e.preventDefault();

        const username = toString(refUsername.current?.value).trim();
        const password = toString(refPassword.current?.value);

        setUsernameError(!username);
        setPasswordError(!password);

        if (!username || !password) {
            return;
        }

        try {
            setLoading(true);

            let user = null;

            if (specialMode) {
                user = await odoo._login(
                    username,
                    password,
                    toString(refUrl.current?.value),
                    toString(refDb.current?.value)
                );
            } else {
                user = await odoo.login(username, password);
            }

            setLoading(false);
            setUser({ mainUser: user });
        } catch (e) {
            setLoading(false);

            console.error(e);

            const metadata = await getMetaData(false);
            console.error(JSON.stringify({ ...metadata }));
            captureMessage('Problème de connexion. Login : ' + username, 'error');

            presentToast({
                ...defaultToastOptions,
                message: `Erreur dans le nom d'utilisateur ou le mot de passe`,
                duration: 3000,
                color: 'danger',
            });
        }
    };

    return (
        <IonPage id="login-page" className="bg-white" style={{ '--ion-background-color': '#ffffff' }}>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>Connexion</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen className="mb-auto content-log-d">
                <IonImg className="mb-8 object-center w-40 mt-5 mx-auto" src={madper} alt="Madper Logo"></IonImg>
                <form noValidate onSubmit={login} className="mx-8">
                    <IonList>
                        <IonItem>
                            <IonInput
                                ref={refUsername}
                                name="username"
                                type="text"
                                defaultValue={user.mainUser?.login || ''}
                                spellCheck={false}
                                autocapitalize="off"
                                required
                                autocomplete="email"
                                labelPlacement="floating"
                                label="Nom d'utilisateur"
                                class="madper-color"
                            />
                        </IonItem>
                        {usernameError && (
                            <IonText color="danger">
                                <p className="ion-padding-start mt-1 ml-2 text-xs">Le nom d'utilisateur est requis</p>
                            </IonText>
                        )}
                        <IonItem className="madper-button">
                            <IonInput
                                ref={refPassword}
                                name="password"
                                type="password"
                                defaultValue={''}
                                spellCheck={false}
                                autocapitalize="off"
                                required
                                autocomplete="current-password"
                                labelPlacement="floating"
                                label="Mot de passe"
                                class="madper-color"
                            />
                        </IonItem>

                        {passwordError && (
                            <IonText color="danger">
                                <p className="ion-padding-start mt-1 ml-2 text-xs">Le mot de passe est requis</p>
                            </IonText>
                        )}

                        {specialMode && (
                            <IonItem>
                                <IonInput
                                    name="url"
                                    type="text"
                                    placeholder="http://localhost:8069"
                                    ref={refUrl}
                                    labelPlacement="floating"
                                    label="URL"
                                    class="madper-color"
                                />
                            </IonItem>
                        )}

                        {specialMode && (
                            <IonItem>
                                <IonInput
                                    name="db"
                                    type="text"
                                    placeholder="odoo-geiq-x"
                                    ref={refDb}
                                    labelPlacement="floating"
                                    label="DB"
                                    class="madper-color"
                                />
                            </IonItem>
                        )}
                    </IonList>
                    <div className="madper-button">
                        <IonButton shape="round" type="submit" expand="block" className="btn">
                            <button>Se connecter</button>
                        </IonButton>
                    </div>
                </form>
                <div>
                    <p
                        className="text-center text-xs mt-4 cursor-pointer"
                        onClick={() => {
                            presentToast({
                                ...defaultToastOptions,
                                message: `Merci de contacter votre GEIQ/GE pour réinitialiser votre mot de passe et recevoir vos nouveaux identifiants.`,
                                duration: 10000,
                                color: 'warning',
                                position: 'bottom',
                            });
                        }}
                    >
                        <span className="text-neutral-500">Nom d'utilisateur ou mot de passe oubliés ?</span>
                    </p>
                </div>
                <div className="flex justify-center">
                    {specialMode && (
                        <IonButton
                            color="danger"
                            shape="round"
                            expand="block"
                            className="btn w-[200px] mt-5"
                            onClick={handleResetAlert}
                        >
                            <button>Réinitialiser l'application</button>
                        </IonButton>
                    )}
                </div>
                <div className="w-28 mx-auto mb-4 mt-16">
                    <p className="text-[0.5rem] text-center">Application propulsée par</p>
                    <IonImg
                        className="mx-3"
                        src={tetra}
                        alt="Tetra Informatique Logo"
                        onClick={() => {
                            refClickNumber.current++;

                            if (refClickNumber.current === 15) {
                                setSpecialMode(true);
                            }
                        }}
                    />
                </div>
            </IonContent>
            <BottomSlotWarning />
        </IonPage>
    );
};

export default Login;
